import React, { useRef, useState } from 'react';
import Header from '../components/Header';
import FormPersonalData, { FormPersonalDataRef } from 'components/FormPersonalData';
import HTMLRenderer from 'components/GlobalHTMLParser';
import { languageMessages } from 'traductor/FirstPayment';
import { Promotion } from 'services/quotation/types';
import { DisplayConfig } from 'services/displayConfig/displayConfigService';
import '../styles/FirstPayment.css';

interface FirstPaymentBrokerProps {
  language: string;
  objectData: any;
  sessionData: any;
  promotion: Promotion | null;
  displayConfig?: DisplayConfig;
  hideToggle?: boolean;
  onClick: () => void;
  onComplete: () => void;
}

const ORDINALS: Record<string, Record<number, string>> = {
  pt: { 2: 'segundo', 3: 'terceiro', 4: 'quarto', 5: 'quinto', 6: 'sexto' },
  es: { 2: 'segundo', 3: 'tercer', 4: 'cuarto', 5: 'quinto', 6: 'sexto' },
  en: { 2: 'second', 3: 'third', 4: 'fourth', 5: 'fifth', 6: 'sixth' },
};

function getOrdinal(month: number, language: string): string {
  return ORDINALS[language]?.[month] ?? String(month);
}

function FirstPaymentBroker({
  language,
  objectData,
  sessionData,
  promotion,
  displayConfig,
  hideToggle = false,
  onClick,
  onComplete,
}: FirstPaymentBrokerProps) {
  const formRef = useRef<FormPersonalDataRef>(null);
  const [isAnnual, setIsAnnual] = useState(false);

  const payButtonText = languageMessages(language, 'makePayment');
  const handlePay = () => formRef.current?.submitForm();

  const renderToggle = () => {
    if (promotion) {
      const startMonth = promotion.free_installments + 1;
      const badgeText = languageMessages(language, 'promotionBadge').replace(
        '{ordinal}',
        getOrdinal(startMonth, language)
      );
      return <span className='fp-promotion-badge'>{badgeText}</span>;
    }
    if (hideToggle) return null;
    return (
      <div className='fp-toggle-row'>
        <span className='fp-toggle-label'>
          {languageMessages(language, 'monthlyLabel')}
        </span>
        <label className='fp-toggle-switch'>
          <input
            type='checkbox'
            checked={isAnnual}
            onChange={() => setIsAnnual(v => !v)}
          />
          <span className='fp-toggle-slider' />
        </label>
        <span className='fp-toggle-label'>
          {languageMessages(language, 'annualLabel')}
        </span>
      </div>
    );
  };

  const renderButtons = () => {
    const partnerButton = displayConfig?.buttons?.find(
      btn => btn.action === 'pay_with_partner' && btn.enable && !btn.hidden
    );
    const partnerStyle = partnerButton
      ? { backgroundColor: partnerButton.button_color, color: partnerButton.primary_color }
      : undefined;

    return (
      <div className='fp-btn-group'>
        <button className='fp-btn-primary' style={partnerStyle} onClick={handlePay}>
          {payButtonText}
        </button>
      </div>
    );
  };

  const renderAmountSection = () => (
    <div className='fp-amount-section'>
      <div className='fp-amount-label'>
        {languageMessages(language, 'premiumToBePaid')}
      </div>
      <div className='fp-amount-value'>
        {objectData?.currency} {sessionData?.amount}
      </div>
      <div className='fp-amount-period'>
        {languageMessages(language, 'perMonth')}
      </div>
    </div>
  );

  return (
    <div className={`app fp-page${promotion ? ' fp-page--promotion' : ''}`}>
      <Header paymentType='first' language={language} showLogo={true} logoUrl='../nomad-olelife.png' />

      {/* ── Two-column content ── */}
      <div className='fp-content'>

        {/* ── Left column: hero + form ── */}
        <div className='fp-left'>
          <div className='fp-hero'>
            <h1 className='fp-title'>
              {displayConfig?.title?.[language as 'en' | 'es' | 'pt']
                ? <span style={{ verticalAlign: 'middle' }} dangerouslySetInnerHTML={{ __html: displayConfig.title[language as 'en' | 'es' | 'pt'] }} />
                : (
                  <>
                    {languageMessages(language, 'promotionHeadlineMain')}
                    <span className='fp-title-green'>
                      {languageMessages(language, 'promotionHeadlineGreen')}
                    </span>
                  </>
                )
              }
              <img
                src='/rounded-green-check.svg'
                alt=''
                className='fp-title-icon'
              />
            </h1>
            <p className='fp-subtitle'>
              {promotion && promotion.free_installments === 1 ? (
                <>
                  <span className='fp-subtitle-desktop-only'>
                    <HTMLRenderer
                      html={`${languageMessages(language, 'promotionSubtextBefore')} <strong>${promotion.free_installments} ${languageMessages(language, 'freeInstallmentsMonthSingular')} ${languageMessages(language, 'promotionSubtextAfter')}</strong>`}
                    />
                  </span>
                  <span className='fp-subtitle-tablet-only'>
                    <HTMLRenderer html={languageMessages(language, 'promotionSubtitleSingular')} />
                  </span>
                  <span className='fp-subtitle-mobile-only'>
                    <HTMLRenderer html={languageMessages(language, 'promotionSubtitleSingularMobile')} />
                  </span>
                </>
              ) : (
                <HTMLRenderer
                  html={
                    promotion
                      ? languageMessages(language, 'promotionSubtitlePlural')
                      : languageMessages(language, 'brokerCardRegistrationSubtitle')
                  }
                />
              )}
            </p>
          </div>

          {objectData && (
            <FormPersonalData
              ref={formRef}
              objectData={objectData}
              onClick={onClick}
              language={language}
              buttonText={payButtonText}
              showRecurrent
              showPaymentMethodTitle={true}
              isFirstPayment
              hideButton={true}
              brokerLayout={true}
              zipCodeLabel="CEP"
            />
          )}

          {objectData?.showOmitPayment && (
            <div className='skip'>
              <span onClick={onComplete} style={{ cursor: 'pointer' }}>
                {languageMessages(language, 'skip')}
              </span>
            </div>
          )}
        </div>

        {/* ── Right column: payment card (desktop only) ── */}
        <div className='fp-right'>
          <div className='fp-payment-card'>
            {renderToggle()}
            {renderAmountSection()}
            {renderButtons()}
          </div>
        </div>
      </div>

      {/* ── Bottom panel: payment card (tablet / mobile) ── */}
      <div className='fp-bottom-panel'>
        {promotion ? (
          <div className='fp-bp-promotion'>
            {renderToggle()}
            {renderAmountSection()}
            {renderButtons()}
          </div>
        ) : (
          <>
            <div className='fp-bp-toggle'>
              {renderToggle()}
            </div>
            <div className='fp-bp-price-row'>
              <div className='fp-amount-label'>
                {languageMessages(language, 'premiumToBePaid')}
              </div>
              <div className='fp-bp-price-right'>
                <div className='fp-amount-value'>
                  {objectData?.currency} {sessionData?.amount}
                </div>
                <div className='fp-amount-period'>
                  {languageMessages(language, 'perMonth')}
                </div>
              </div>
            </div>
            <div className='fp-bp-btns'>
              {renderButtons()}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default FirstPaymentBroker;
